import { useCallback, useRef, useState } from "react";
import {
  type ApiErrorInfo,
  AUTH_ERROR_CODE,
  NETWORK_ERROR,
  parseApiError,
  SYSTEM_ERROR,
  UNAUTHORIZED_EVENT,
} from "./apiError";

export type ApiRequestResult<T> =
  | { ok: true; data: T | undefined }
  | { ok: false; error: ApiErrorInfo };

interface UseApiRequestResult<T> {
  isLoading: boolean;
  error: ApiErrorInfo | null;
  /**
   * fetchを実行し、成功/失敗を判別可能な形で結果を返す。
   * レスポンスボディが空（204、またはvoidを返すAPIの200）の場合はok:trueでdata:undefined。
   * 実行中に新たな`execute`呼び出しが発生した場合、isLoading/errorは常に
   * 最後に呼び出されたexecuteの結果のみを反映する（古い呼び出しの完了では上書きしない）。
   * ただし各`execute`呼び出しが返すPromiseは、実行中に別の呼び出しが割り込んでも
   * 常にその呼び出し自身の実際の結果を返す。
   *
   * 注意: レスポンスボディを`T`として解釈する処理はランタイム検証を行わない
   * 型アサーション（`JSON.parse(...) as T`）であり、実際のJSON形状が`T`と
   * 一致する保証はない。バックエンドのレスポンス契約が変わった場合、ここではなく
   * 利用箇所で実行時エラーとして顕在化する点に注意すること。
   */
  execute: (input: RequestInfo, init?: RequestInit) => Promise<ApiRequestResult<T>>;
  clearError: () => void;
}

const useApiRequest = <T = unknown>(): UseApiRequestResult<T> => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiErrorInfo | null>(null);
  const requestIdRef = useRef(0);

  const execute = useCallback(
    async (input: RequestInfo, init?: RequestInit): Promise<ApiRequestResult<T>> => {
      const requestId = ++requestIdRef.current;
      const isLatest = () => requestId === requestIdRef.current;

      setIsLoading(true);
      setError(null);

      let res: Response;
      try {
        res = await fetch(input, {
          credentials: "include",
          ...init,
          headers: {
            "Content-Type": "application/json",
            ...init?.headers,
          },
        });
      } catch {
        if (isLatest()) {
          setError(NETWORK_ERROR);
          setIsLoading(false);
        }
        return { ok: false, error: NETWORK_ERROR };
      }

      if (!res.ok) {
        const apiError = await parseApiError(res);
        if (apiError.code === AUTH_ERROR_CODE) {
          window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
        }
        if (isLatest()) {
          setError(apiError);
          setIsLoading(false);
        }
        return { ok: false, error: apiError };
      }

      if (isLatest()) {
        setIsLoading(false);
      }

      // voidを返すAPI（登録/更新/削除等）は200かつ空ボディのため、
      // res.json()を直接呼ぶとパース失敗になる。先にテキストで空かどうかを見る。
      const text = await res.text();
      if (!text) {
        return { ok: true, data: undefined };
      }

      try {
        return { ok: true, data: JSON.parse(text) as T };
      } catch {
        if (isLatest()) {
          setError(SYSTEM_ERROR);
        }
        return { ok: false, error: SYSTEM_ERROR };
      }
    },
    [],
  );

  const clearError = useCallback(() => setError(null), []);

  return { isLoading, error, execute, clearError };
};

export default useApiRequest;
